import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'utils/makeStylesAdapter';
import { ButtonConsensus, ProsecutorOfficeAndProsecutorForm } from 'components';
import { colors } from 'utils/colorsSteps';
import { onCloseWithBackdropDialog } from 'utils/materialUI/backdropClickDialog';
import { useSelector, useDispatch } from 'react-redux';
import { USER_TYPE } from 'constants/inquirys/userType';
import { toast } from 'react-toastify';
import { setRequestIntimationsReducer } from 'actions';
import intimationService from 'services/intimation/intimationService';
import IntimationService from 'services/intimation/intimationService';
import { CERTIFICATE_TYPES } from 'constants/investigated-certificate-types';
import WarningIcon from '@mui/icons-material/Warning';

const useStyles = makeStyles((theme) => ({
  alertContainer: {
    backgroundColor: ' #fffdf5',
    borderLeft: '4px solid #fff1d0',
    padding: theme.spacing(2),
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  alertTitle: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: ' #795548',
  },
  alertIcon: {
    marginRight: theme.spacing(1),
  },
  alertMessage: {
    marginBottom: theme.spacing(1),
    color: ' #4e342e',
  },
  warningItem: {
    display: 'flex',
    alignItems: 'center',
    color: 'black',
  },
}));


const ModalIntimationAutomatic = props => {
  const {
    open,
    onClose,
    className,
    inquiry,
    loading,
    setLoading,
    recipient,
    intimationCertificate,
    victim,
    selectedType,
    getCertificates,
    getIntimations,
    ...rest
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const loggedUser = useSelector(state => state.logedUser);
  const isProsecutor = loggedUser.type === USER_TYPE.Prosecutor;
  const dispatch = useDispatch();

  const [formState, setFormState] = useState({
    prosecutor: isProsecutor
      ? { ...loggedUser, id: loggedUser.userId }
      : inquiry?.inquiry?.prosecutor,
    prosecutorOffice: inquiry?.inquiry?.prosecutorOffice || null,
  });

  const [warnings, setWarnings] = useState([]);

  const getWarnings = async () => {
    if (![CERTIFICATE_TYPES.INVESTIGADO, CERTIFICATE_TYPES.VITIMA].includes(selectedType)) {
      setWarnings([]);
      return;
    }
    setLoading(true);
    const warnings = await IntimationService.validateIntimationModes(
      inquiry?.id,
      inquiry?.inquiry?.prosecutorOfficeGroup?.id,
    );
    const recipientId = selectedType === CERTIFICATE_TYPES.VITIMA ? victim?.id : inquiry?.investigated?.id;
    setWarnings(warnings.data.warnings.filter(w => w.type === selectedType && w.id === recipientId));
    setLoading(false);
  };

  useEffect(() => {
    getWarnings();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFormState({
        prosecutor: isProsecutor
          ? { ...loggedUser, id: loggedUser.userId }
          : inquiry?.inquiry?.prosecutor,
        prosecutorOffice: inquiry?.inquiry?.prosecutorOffice || null,
      });
    }
  }, [open]);

  const handleChangeProsecutor = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      prosecutor: newValue,
    }));
  };

  const handleChangeProsecutorOffice = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      prosecutorOffice: newValue,
    }));
  };

  const handleSubmit = () => {
    if (!formState.prosecutor) {
      toast.warn('Promotor obrigatório');
      return;
    }

    if (!formState.prosecutorOffice) {
      toast.warn('Promotoria obrigatória');
      return;
    }

    setLoading(true);

    let params = {
      inquiryInvestigatedId: inquiry.id,
      prosecutor: formState.prosecutor,
      prosecutorOffice: formState.prosecutorOffice,
      recipient,
      investigatedCertificateId: intimationCertificate?.id || null,
      victimId: victim?.id || null,
    };

    intimationService
      .createIntimationToArchiving(params)
      .then(() => {
        dispatch(setRequestIntimationsReducer(true));
        toast.success('Intimação realizada com sucesso');
        getCertificates();
        getIntimations();
        onClose();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      <Dialog
        fullScreen={fullScreen}
        onClose={(event, reason) => onCloseWithBackdropDialog(event, reason, onClose)}
        open={open}
        aria-labelledby="responsive-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Confirmar Ação para Tipo {selectedType}
          {selectedType === 'Vítima' && victim?.name && ` (${victim.name})`}
        </DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ProsecutorOfficeAndProsecutorForm
                prosecutor={formState.prosecutor}
                prosecutorOffice={formState.prosecutorOffice}
                onProsecutorChange={handleChangeProsecutor}
                onProsecutorOfficeChange={handleChangeProsecutorOffice}
                isProsecutor={isProsecutor}
                prosecutorOfficeGroup={inquiry?.inquiry?.prosecutorOfficeGroup}
              />
            </ListItem>
          </List>  
          <List className={loading ? '' : classes.alertContainer}>
            {(!loading && (warnings.length > 0 || selectedType === CERTIFICATE_TYPES.DELEGADO)) ? (
              <>
                <ListItem disableGutters>
                  <Typography variant="h6" className={classes.alertTitle}>
                    <WarningIcon className={classes.alertIcon} />
                    Avisos:
                  </Typography>
                </ListItem>

                <ListItem disableGutters>
                  <Typography variant="body1" className={classes.alertMessage}>
                    {selectedType === CERTIFICATE_TYPES.DELEGADO
                      ? 'A intimação será enviada para o email do Órgão de Origem'
                      : 'Você tem certeza que deseja realizar a intimação?'}
                  </Typography>
                </ListItem>

                {warnings.map((warning, index) => (
                  <ListItem key={index} disableGutters>
                    <Typography variant="body2" className={classes.warningItem}>
                      {warning.message}
                    </Typography>
                  </ListItem>
                ))}
              </>
            ) : null}
          </List>
        </DialogContent>
        <DialogActions>
          <ButtonConsensus
            title="Cancelar"
            backgroundColor="#fff"
            hovercolor={colors.greySecundary}
            color={colors.greyShadow}
            onClick={onClose}
          />
          <ButtonConsensus
            title="Confirmar"
            backgroundColor={colors.primary}
            hovercolor={colors.primary}
            onClick={handleSubmit}
            disabled={loading}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ModalIntimationAutomatic;
